// ClassStudentEditor.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Camera, CheckCircle2, Edit3, Save } from 'lucide-react';
import useClassAccess from '../../hooks/useClassAccess';
import { showToast } from '../../utils/feedback';
import Spinner from '../Common/Spinner';
import { compressImage } from '../../utils/imageCompress';
import './ClassAccess.css';

const EDITABLE_FIELDS = [
  ['division', 'Division', 'select'],
  ['custom_div', 'Custom Division', 'text'],
  ['serial_no', 'Serial No', 'number'],
  ['student_name', 'Student Name', 'text'],
  ['fathers_name', "Father's Name", 'text'],
  ['dob', 'Date of Birth', 'text'],
  ['reg_no', 'Reg No', 'text'],
  ['appar_id', 'Pen No / Appar Id', 'text'],
  ['sat_no', 'SAT No', 'text'],
  ['house', 'House', 'select'],
  ['address', 'Address', 'textarea'],
  ['mobile', 'Mobile', 'text'],
];

const HOUSE_OPTIONS = ['Red', 'Green', 'Blue', 'Yellow'];
const DIVISION_OPTIONS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
];
const MAX_PHOTO_SIZE = 8 * 1024 * 1024;

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(new Error('Unable to read the selected photo.'));
    reader.readAsDataURL(blob);
  });

const createFormData = (student = {}) =>
  EDITABLE_FIELDS.reduce((result, [key]) => {
    result[key] = student?.[key] ?? '';
    return result;
  }, {});

const ClassStudentEditor = ({ token }) => {
  const { loading, getClassStudents, updateClassStudent } = useClassAccess();
  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [formData, setFormData] = useState(createFormData());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const currentStudent = students[currentIndex] || null;

  const loadClass = useCallback(async () => {
  if (!token) {
    setError('This class link is invalid.');
    return;
  }

  try {
    setError('');

    const result = await getClassStudents(token);

    setClassInfo(result?.classInfo || null);

    const orderedStudents = Array.isArray(result?.students)
      ? result.students
      : [];

    // The Cloud Function is the single source of truth
    // for student display order.
    setStudents(orderedStudents);
    setCurrentIndex(0);
  } catch (requestError) {
    console.error('Open class link error:', requestError);

    setError(
      requestError?.message ||
        'This class link is invalid, disabled, or expired.'
    );
  }
}, [getClassStudents, token]);

  useEffect(() => {
    loadClass();
  }, [loadClass]);

  useEffect(() => {
    setFormData(createFormData(currentStudent || {}));
    setSelectedPhoto(null);
    setPhotoPreview('');
    setIsEditing(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStudent]);

  useEffect(() => () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  // വാണിംഗ് പരിഹരിച്ച ഭാഗം ഇവിടെയാണ്
  const fieldPermissions = useMemo(() => {
    return classInfo?.fieldPermissions || {};
  }, [classInfo?.fieldPermissions]);

  const visibleFields = useMemo(
    () => EDITABLE_FIELDS.filter(([key]) => fieldPermissions[key] === true),
    [fieldPermissions]
  );

  const canManagePhoto = fieldPermissions.photo_url === true || fieldPermissions.photo_name === true;
  const showStandard = fieldPermissions.std_roman === true;
  //const showDivision = fieldPermissions.division === true || fieldPermissions.custom_div === true;
  const showDivision = false;

  const progress = useMemo(() => {
    if (!students.length) return 0;
    return Math.round(((currentIndex + 1) / students.length) * 100);
  }, [currentIndex, students.length]);

  const handleChange = (field, value) => {
    if (!isEditing) return;
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const handlePhotoChange = (event) => {
    if (!isEditing || !canManagePhoto) return;
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast({ icon: 'warning', title: 'Please select a valid image file.' });
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      showToast({ icon: 'warning', title: 'Please select an image smaller than 8 MB.' });
      return;
    }

    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setSelectedPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const saveStudent = async () => {
    if (!currentStudent) return;
    if (!String(formData.student_name || '').trim()) {
      showToast({ icon: 'warning', title: 'Student name is required.' });
      return;
    }

    try {
      setSaving(true);
      const updates = visibleFields.reduce((result, [key]) => {
        const value = formData[key];
        result[key] = key === 'serial_no'
          ? (value === '' ? null : Number(value))
          : String(value ?? '').trim();
        return result;
      }, {});

      if (Object.prototype.hasOwnProperty.call(updates, 'student_name')) {
        updates.student_name = String(updates.student_name || '').trim();
      }

      let photoPayload = null;
      if (selectedPhoto && canManagePhoto) {
        const compressedPhoto = await compressImage(selectedPhoto, {
          quality: 0.7,
          maxSide: 1600,
        });
        const base64 = await blobToBase64(compressedPhoto);
        photoPayload = {
          base64,
          contentType: 'image/png',
          fileName: `${String(selectedPhoto.name || 'student-photo').replace(/\.[^/.]+$/, '')}.png`,
        };
      }

      const result = await updateClassStudent(
        token,
        currentStudent.id,
        updates,
        photoPayload
      );

      setStudents((previous) =>
        previous.map((student, index) =>
          index === currentIndex
            ? {
                ...student,
                ...updates,
                ...(result?.photo_url ? { photo_url: result.photo_url } : {}),
                ...(result?.photo_name ? { photo_name: result.photo_name } : {}),
              }
            : student
        )
      );

      setSelectedPhoto(null);
      setPhotoPreview('');
      setIsEditing(false);
      showToast({ icon: 'success', title: 'Student updated successfully.' });
    } catch (requestError) {
      console.error('Class student update error:', requestError);
      showToast({ icon: 'error', title: requestError?.message || 'Failed to update student.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !classInfo && !error) {
    return <div className="class-public-state"><Spinner label="Opening class..." /></div>;
  }

  if (error) {
    return (
      <div className="class-public-state">
        <div className="class-public-message">
          <h2>Link unavailable</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!students.length) {
    return (
      <div className="class-public-state">
        <div className="class-public-message">
          <CheckCircle2 size={42} />
          <h2>No students found</h2>
          <p>This class currently has no student records.</p>
        </div>
      </div>
    );
  }

  return (
    <main className="class-editor-page">
      <header className="class-editor-header">
        <div>
          <span className="class-editor-school">{classInfo?.schoolName || 'School'}</span>
          <h1>Class {classInfo?.label}</h1>
        </div>
        <div className="class-editor-counter">{currentIndex + 1} / {students.length}</div>
      </header>

      <div className="class-editor-progress" aria-label={`${progress}% complete`}>
        <span style={{ width: `${progress}%` }} />
      </div>

      <section className="class-editor-card">
        <div className="class-editor-student-heading">
          {photoPreview || currentStudent?.photo_url ? (
            <img src={photoPreview || currentStudent.photo_url} alt={currentStudent.student_name || 'Student'} />
          ) : (
            <div className="class-editor-avatar">{String(currentStudent?.student_name || '?').charAt(0).toUpperCase()}</div>
          )}
          <div>
            <small>Student {currentIndex + 1}</small>
            <h2>{currentStudent?.student_name || 'Unnamed Student'}</h2>
            {isEditing && canManagePhoto && (
              <label className="class-photo-change-btn">
                <Camera size={15} />
                <span>{selectedPhoto ? 'Photo selected' : 'Upload / Change Photo'}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  disabled={saving}
                />
              </label>
            )}
          </div>
        </div>

        {(showStandard || showDivision) && (
          <div className="class-locked-fields">
            {showStandard && (
              <div><span>Standard</span><strong>{classInfo?.std_roman || '-'}</strong></div>
            )}
            {showDivision && (
              <div><span>Division</span><strong>{classInfo?.custom_div || classInfo?.division || '-'}</strong></div>
            )}
          </div>
        )}

        <div className="class-editor-form">
          {visibleFields.map(([key, label, type]) => (
            <label className={type === 'textarea' ? 'class-editor-field class-editor-field--full' : 'class-editor-field'} key={key}>
              <span>{label}{key === 'student_name' ? ' *' : ''}</span>
              {type === 'textarea' ? (
                <textarea rows="3" value={formData[key]} readOnly={!isEditing} onChange={(event) => handleChange(key, event.target.value)} />
              ) : type === 'select' ? (
                                <select
                    value={formData[key] || ''}
                    disabled={!isEditing || saving}
                    onChange={(e) =>
                        setFormData((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                        }))
                    }
                >
                    {(key === 'house'
                        ? HOUSE_OPTIONS
                        : DIVISION_OPTIONS
                    ).map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
              ) : (
                <input type={type} value={formData[key]} readOnly={!isEditing} onChange={(event) => handleChange(key, event.target.value)} />
              )}
            </label>
          ))}
        </div>
      </section>

      <div className="class-editor-actions">
        <button type="button" className="class-nav-btn" onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))} disabled={currentIndex === 0 || saving}>
          <ArrowLeft size={18} /> Previous
        </button>
        {isEditing ? (
          <button type="button" className="class-save-btn" onClick={saveStudent} disabled={saving}>
            {saving ? <Spinner label="Saving..." small /> : <><Save size={18} /> Save</>}
          </button>
        ) : (
          <button type="button" className="class-save-btn" onClick={() => setIsEditing(true)} disabled={saving}>
            <Edit3 size={18} /> Edit
          </button>
        )}
        <button
          type="button"
          className="class-next-btn"
          onClick={() => setCurrentIndex((index) => Math.min(students.length - 1, index + 1))}
          disabled={currentIndex === students.length - 1 || saving}
        >
          Next <ArrowRight size={18} />
        </button>
      </div>
    </main>
  );
};

export default ClassStudentEditor;